/**
 * @fileOverview 日期相关处理(2019-11-26)
 * @author nicywang <nicywang@tencent.com>
 * @module utils/date
 */
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

const _MS_PER_DAY = 1000 * 60 * 60 * 24;
const _CUR_DAY = new Date();

export const formatDate = function (date, symbol) {
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join(symbol);
};

export const quarterDates = () => {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const firstDate = new Date(now.getFullYear(), quarter * 3, 1);
  const endDate = new Date(firstDate.getFullYear(), firstDate.getMonth() + 3, 0);
  const start = formatDate(firstDate, '-');
  const end = formatDate(endDate, '-');

  return [start, end];
};

export const dateDiffInDays = function (target, source) {
  const a = new Date(target);
  const b = new Date(source);
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
};

/**
 * @description: 获取当前月份第一天0时
 */
export const getMonthFirstDay = () => {
  const year = dayjs().year();
  const month = dayjs().month();
  const date = `${year}-${month + 1}-01`;
  return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
};

// 判断是否是日期格式并返回
export const isDate = (date) => {
  if (isNaN(date) && !isNaN(Date.parse(date))) {
    return date;
  }
};

/**
 * 获取最近一个季度末的一天
 * @param {string | Dayjs} date 如
 * @param {string | Dayjs} minDate {} 若目标日期<minDate，则返回minDate
 * @return {string} 如 2020-10-25
 */
export const getQuarterFirstDay = (date, minDate = '') => {
  const target = dayjs(date).startOf('quarter');
  if (minDate) {
    const min = dayjs(minDate);
    return (min.isAfter(target) ? min : target).format('YYYY-MM-DD');
  }
  return target.format('YYYY-MM-DD');
};

export const getQuarterRange = (quarter, year, format) => {
  const yearUsed = year || dayjs().year();
  const startMonth = (quarter - 1) * 3;
  const startDate = dayjs(new Date(yearUsed, startMonth, 1));
  const endDate = startDate.add(3, 'month').subtract(1, 'day');

  return {
    startDate: startDate.format(format || 'YYYY-MM-DD'),
    endDate: endDate.format(format || 'YYYY-MM-DD'),
  };
};

const getWeekDay = (date) => {
  let weekDay = date.day();
  return weekDay === 0 ? 7 : weekDay;
};

// 获取某日期所属季度的自然周
export const getWeeksOfQuarter = (date, untillNow = true, format = 'YYYY-MM-DD') => {
  const curQuarter = dayjs(date).quarter();
  // 季度开始日期
  let min = dayjs(date).startOf('quarter')
    .startOf('week');
  if (min.quarter() !== curQuarter && getWeekDay(min) > 3) {
    min = min.add(1, 'week');
  }
  // 季度结束日期
  let max = untillNow ? dayjs(date).endOf('week') : dayjs(date).endOf('quarter')
    .endOf('week');
  if (max.quarter() !== curQuarter && getWeekDay(max) > 3) {
    max = max.subtract(1, 'week');
  }
  const weeks = [];
  let cur = min;
  while (cur <= max) {
    weeks.push({
      startDate: cur.format(format),
      endDate: cur.add(6, 'day').format(format),
    });
    cur = cur.add(7, 'day');
  }
  return weeks;
};

// 获取当前季度的自然周日期
export const getWeekDateOfQuarter = (date, untillNow = true, format = 'YYYY-MM-DD') => {
  const curQuarter = dayjs(date).quarter();
  // 季度开始日期
  let min = dayjs(date).startOf('quarter')
    .startOf('week');
  if (min.quarter() !== curQuarter && getWeekDay(min) > 3) {
    min = min.add(1, 'week');
  }
  // 季度结束日期，截止当前周
  let max = untillNow ? dayjs(date).endOf('week') : dayjs(date).endOf('quarter')
    .endOf('week');
  if (max.quarter() !== curQuarter && getWeekDay(max) > 3) {
    max = max.subtract(1, 'week');
  }
  // 季度结束日期
  let quarterEnd = dayjs(date).endOf('quarter')
    .endOf('week');
  if (quarterEnd.quarter() !== curQuarter && getWeekDay(quarterEnd) > 3) {
    quarterEnd = quarterEnd.subtract(1, 'week');
  }
  return {
    startDate: min.format(format),
    endDate: max.format(format),
    quarterEndDate: quarterEnd.format(format),
  };
};

// 获取当前季度的时间进度
export const getQuarterProgress = (date) => {
  const curDate = dayjs(date);
  const quarterStart = curDate.startOf('quarter');
  const quarterEnd = curDate.endOf('quarter');

  const qtd = curDate.diff(quarterStart, 'day') + 1;
  const quarterDays = quarterEnd.diff(quarterStart, 'day') + 1;

  return qtd / quarterDays;
};

export const getQuarterList = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const startYear = start.year();
  const endYear = end.year();
  const startQuarter = start.quarter();
  const endQuarter = end.quarter();
  const quarterList = [];
  let curYear = startYear;
  let curQuarter = startQuarter;
  while (curYear <= endYear) {
    const curEnd = curYear === endYear ? endQuarter : 4;
    while (curQuarter <= curEnd) {
      quarterList.push(`${curYear}Q${curQuarter}`);
      curQuarter += 1;
    }
    curYear += 1;
    curQuarter = 1;
  }
  return quarterList;
};

/**
 * 默认文件导出
 */
export default {
  quarterDates,
  dateDiffInDays,
  isDate,
  getQuarterFirstDay,
  getQuarterRange,
  getQuarterList,
};
